import { Record as record, List, Map } from 'immutable';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { listOf } from 'redux-immutable-tools';
import dateFormatMap from '../dateformats';

import { decorateValidation, isRequired, isValidEmail } from './validation';

export var UserTrialUnitAccess = record({
  trialUnitId: null,
  status: null
});

var Trial = record({
  id: null,
  name: null
});

var Site = record({
  id: null,
  siteAlias: null
});
export var TrialSiteStatusShape = ImmutablePropTypes.recordOf({
  userTrialSiteAccessId: PropTypes.number,
  status: PropTypes.string,
  trial: Trial,
  site: Site
});

export var TrialSiteStatus = record({
  userTrialSiteAccessId: null,
  status: null,
  trial: Trial,
  site: Site,
  allowedToApprove: null
});

export var TrialSiteAccessShape = ImmutablePropTypes.recordOf({
  id: PropTypes.number,
  approved: PropTypes.bool,
  personnelNo: PropTypes.string,
  allowedToApprove: PropTypes.bool,
  trial: Trial,
  site: Site
});

export var TrialSiteAccess = record({
  id: null,
  approved: null,
  personnelNo: null,
  allowedToApprove: null,
  trial: Trial,
  site: Site
});

export var User = record({
  id: null,
  username: null,
  fullName: null,
  email: null,
  phone: null,
  accessGroups: List,
  status: null,
  trialUnits: listOf(UserTrialUnitAccess),
  unapprovedTrialSiteAccess: listOf(TrialSiteStatus),
  companyTvnId: null,
  companyName: null,
  officeName: null,
  loginUsername: null,
  accessRights: List,
  timezone: 'UTC',
  timezoneOffset: 0,
  dateFormat: null,
  lastLogin: null,
  dateTimeFormat: null,
  ccisVersion: null,
  externalIp: null,
  country: null,
  trialSiteStatusList: listOf(TrialSiteStatus),
  passwordStrength: null,
  currentPassword: null,
  newPassword: null,
  confirmPassword: null,
  meta: Map
});

export var PendingUser = record({
  id: null,
  fullName: null,
  email: null,
  countryCode: null,
  discontinued: null,
  invitationSentDate: null,
  officeId: null,
  personnelNo: null,
  status: null,
  trialSiteAccesses: listOf(TrialSiteAccess)
});

export var UserTrialSiteAccess = record({
  id: null,
  user: User,
  trial: Trial,
  site: Site,
  status: null
});

User.normalize = function (data) {
  var _ref = Reflect.has(dateFormatMap, data.dateFormat) ? dateFormatMap[data.dateFormat] : {
    dateFormat: data.dateFormat,
    dateTimeFormat: data.dateFormat,
    dateTimeFormatTz: data.dateFormat
  },
      dateFormat = _ref.dateFormat,
      dateTimeFormat = _ref.dateTimeFormat,
      dateTimeTzFormat = _ref.dateTimeTzFormat;

  var normalized = Object.assign({}, data, {
    timezone: data.timeZone,
    timezoneOffset: data.timeZoneOffset,
    dateFormat: dateFormat,
    dateTimeFormat: dateTimeFormat,
    dateTimeTzFormat: dateTimeTzFormat
  });
  return normalized;
};

PendingUser.normalize = function (data) {

  var normalized = Object.assign({}, data);
  return normalized;
};

UserTrialSiteAccess.normalize = function (data) {

  var normalized = Object.assign({
    id: data.userTrialSiteAccessId
  }, data);
  return normalized;
};

User.prototype.hasCcisManagementAccess = function hasCcisAccess() {
  return this.accessRights.contains('ccis');
};
User.prototype.hasCompanyManagerAccess = function hasCompanyManagerAccess() {
  return this.accessRights.contains('tvnadmin');
};
User.prototype.hasCompanyUserManagerAccess = function hasCompanyUserManagerAccess() {
  return this.accessRights.contains('c_user_manager');
};
User.prototype.hasTrialManagementAccess = function hasTrialManagementAccess() {
  return this.accessRights.contains('clinical_trial_management');
};
User.prototype.hasCreateTrialAccess = function hasCreateTrialAccess() {
  return this.accessRights.contains('clinical_create_trial');
};
User.prototype.hasEditTrialAccess = function hasEditTrialAccess() {
  return this.accessRights.contains('clinical_edit_trial');
};
User.prototype.hasReviewTrialAccess = function hasReviewTrialAccess() {
  return this.accessRights.contains('clinical_review_trial');
};
User.prototype.hasApproveTrialAccess = function hasApproveTrialAccess() {
  return this.accessRights.contains('clinical_approve_trial');
};
User.prototype.hasLoggerManagementAccess = function hasLoggerManagementAccess() {
  return this.accessRights.contains('clinical_logger_management');
};
User.prototype.hasCreateLoggerAccess = function hasCreateLoggerAccess() {
  return this.accessRights.contains('clinical_create_logger');
};
User.prototype.hasEditLoggerAccess = function hasEditLoggerAccess() {
  return this.accessRights.contains('clinical_edit_logger');
};
User.prototype.hasReportsAccess = function hasReportsAccess() {
  return this.accessRights.contains('jreport');
};
User.prototype.hasGeotrackAccess = function hasGeotrackAccess() {
  return this.accessRights.contains('geotrack');
};
User.prototype.hasTvnAccess = function hasTvnkAccess() {
  return this.accessRights.contains('tvnuser');
};
User.prototype.hasTTCWebAccess = function hasTTCWebAccess() {
  return this.accessRights.contains('ttwc');
};
User.prototype.hasImsAccess = function hasImsAccess() {
  return this.accessRights.contains('incident_management');
};
User.prototype.hasFacilityAccess = function hasFacilityAccess() {
  return this.accessRights.contains('facility');
};
User.prototype.hasTSSAdminAccess = function hasTSSAdminAccess() {
  return this.accessRights.contains('sysadmin');
};
User.prototype.hasRmaAccess = function hasRmaAccess() {
  return this.accessRights.contains('rma') || this.accessRights.contains('order');
};
User.prototype.hasCompanySettingsAccess = function hasCompanySettingsAccess() {
  return this.accessRights.contains('tvnsuper') || this.accessRights.contains('tvnadmin') || this.accessRights.contains('c_user_manager');
};
User.prototype.hasClinicalAccess = function hasClinicalAccess() {
  return this.accessRights.contains('clinical');
};
User.prototype.hasProfileManagementAccess = function hasProfileManagementAccess() {
  return this.accessRights.contains('clinical_profile_management');
};
User.prototype.hasCreateProfileAccess = function hasCreateProfileAccess() {
  return this.accessRights.contains('clinical_create_profile');
};
User.prototype.hasEditProfileAccess = function hasEditProfileAccess() {
  return this.accessRights.contains('clinical_edit_profile');
};
User.prototype.hasApproveProfileAccess = function hasApproveProfileAccess() {
  return this.accessRights.contains('clinical_approve_profile');
};
User.prototype.hasReviewProfileAccess = function hasReviewProfileAccess() {
  return this.accessRights.contains('clinical_review_profile');
};

User.prototype.hasDeviationManagementAccess = function hasDeviationManagementAccess() {
  return this.accessRights.contains('clinical_deviation_management');
};

User.prototype.hasReviewDeviationAccess = function hasReviewDeviationAccess() {
  return this.accessRights.contains('clinical_review_deviation');
};

User.prototype.hasCreateDeviationAccess = function hasCreateDeviationAccess() {
  return this.accessRights.contains('clinical_create_deviation');
};

User.prototype.hasEditDeviationAccess = function hasEditDeviationAccess() {
  return this.accessRights.contains('clinical_edit_deviation');
};

User.prototype.hasApproveDeviationAccess = function hasApproveDeviationAccess() {
  return this.accessRights.contains('clinical_approve_deviation');
};

User.prototype.hasUploadAccess = function hasUploadAccess() {
  return this.accessRights.contains('upload');
};

User.prototype.hasUploadShipmentsAccess = function hasUploadShipmentsAccess() {
  return this.accessRights.contains('upload_shipments');
};

User.prototype.hasUploadStorageAccess = function hasUploadStorageAccess() {
  return this.accessRights.contains('upload_storage');
};

User.prototype.hasUploadArchiveAccess = function hasUploadArchiveAccess() {
  return this.accessRights.contains('upload_archive');
};

User.prototype.hasUserManagementAccess = function hasUserManagementAccess() {
  return this.accessRights.contains('user_management');
};

User.prototype.hasAuditTrailAccess = function hasAuditTrailAccess() {
  return this.accessRights.contains('company_audit_trail');
};

export var UserTrialUnitAccessShape = ImmutablePropTypes.recordOf({
  trialUnitId: PropTypes.number,
  status: PropTypes.string
});

export var UserShape = ImmutablePropTypes.recordOf({
  id: PropTypes.number,
  username: PropTypes.string,
  fullName: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
  accessGroups: ImmutablePropTypes.listOf(PropTypes.string),
  companyTvnId: PropTypes.string,
  companyName: PropTypes.string,
  loginUsername: PropTypes.string,
  officeName: PropTypes.string,
  lastLogin: PropTypes.number,
  accessRights: PropTypes.object,
  timezone: PropTypes.string,
  timezoneOffset: PropTypes.number,
  trialUnits: ImmutablePropTypes.listOf(UserTrialUnitAccessShape),
  unapprovedTrialSiteAccess: ImmutablePropTypes.listOf(TrialSiteStatus),
  trialSiteStatusList: ImmutablePropTypes.listOf(TrialSiteStatusShape),
  dateFormat: PropTypes.string,
  ccisVersion: PropTypes.string,
  externalIp: PropTypes.string,
  country: PropTypes.string,
  passwordStrength: PropTypes.number,
  currentPassword: PropTypes.string,
  newPassword: PropTypes.string,
  confirmPassword: PropTypes.string
});

export var PendingUserShape = ImmutablePropTypes.recordOf({
  id: PropTypes.number,
  fullName: PropTypes.string,
  email: PropTypes.string,
  countryCode: PropTypes.string,
  discontinued: PropTypes.bool,
  invitationSentDate: PropTypes.number,
  officeId: PropTypes.string,
  personnelNo: PropTypes.string,
  status: PropTypes.string,
  trialSiteAccesses: ImmutablePropTypes.listOf(TrialSiteAccessShape)
});

export var UserTrialSiteAccessShape = ImmutablePropTypes.recordOf({
  id: null,
  user: User,
  trial: Trial,
  site: Site,
  status: null
});

User.prototype.checkPasswordStrength = function checkPasswordStrength(password) {
  /* function to check the password strength */
  var pwd = password.trim();
  var score = 0;
  if (pwd.length >= 4) {
    /* 4 or more chars -> minimal */
    score += 1;
    if (pwd.length >= 6) {
      /* 6 or more chars -> normal */
      score += 1;
      if (pwd.length >= 8) {
        /* 8 or more chars -> better */
        score += 1;
        if (pwd.match(/[a-z]/) && pwd.match(/[A-Z]/)) {
          /* both upper- and lower case chars */
          score += 1;
        }
        if (pwd.match(/[a-zA-Z]+/) && pwd.match(/\d+/)) {
          /* at least one number */
          score += 1;
        }
        if (pwd.match(/.[!,@,#,$,%,^,&,*,?,_,~,-,(,)]/)) {
          /* at least one special character */
          score += 1;
        }
      }
    }
  }
  return score;
};

export function isPasswordValid(value) {
  return (
    // Empty string
    value !== '' &&
    // Not null
    value !== null &&
    // Not undefined
    value !== undefined &&
    // Not an array, or if array it contains at least one item
    this.checkPasswordStrength(value) >= this.passwordStrength ? null : 'validation.error.password.to.weak'
  );
}

export var UserShapeList = ImmutablePropTypes.listOf(UserShape);
export var PendingUserShapeList = ImmutablePropTypes.listOf(PendingUserShape);
export var UserTrialSiteAccessShapeList = ImmutablePropTypes.listOf(UserTrialSiteAccessShape);

decorateValidation(User, {
  email: [isRequired, isValidEmail],
  currentPassword: [],
  newPassword: [],
  confirmPassword: []
});